import {
  Alert,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { Check } from "@mui/icons-material";
import CustomDialog from "app/components/CustomDialog";
import { useEffect, useState } from "react";


const getInitialFormState = (user = {}, plan = null) => ({
  name: user?.name || user?.displayName || user?.jp_name || "",
  email: user?.email || "",
  selectedPlan: plan?.name || ""
});

export default function PlanInquiryDialog({ open, onClose, plan, user, onSubmit }) {
  const [form, setForm] = useState(getInitialFormState(user, plan));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm(getInitialFormState(user, plan));
      setErrors({});
    }
  }, [open, user, plan]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const nextErrors = {};

    if (!form.name.trim()) {
      nextErrors.name = "Name is required.";
    }

    if (!form.email.trim()) {
      nextErrors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      nextErrors.email = "Enter a valid email address.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    onSubmit?.({
      name: form.name.trim(),
      email: form.email.trim(),
      planId: plan?.id || "",
      planName: plan?.name || "",
      planPrice: plan?.price ?? null,
      planDuration: plan?.duration || ""
    });
  };

  const planPriceLabel =
    typeof plan?.price === "number" ? `Rs. ${plan.price} ${plan?.duration || ""}` : null;

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      dialogTitle="Plan Inquiry"
      paperPropsSx={{ borderRadius: 4 }}
    >
      <DialogContent dividers>
        <Stack spacing={3}>
          <Box>
            <Typography variant="body1" fontWeight={600} color="text.primary" mb={1}>
              Selected Plan
            </Typography>
            <Stack direction="column" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label={plan?.name || "No plan selected"}
                color="primary"
                variant="filled"
                sx={{ fontWeight: 600, maxWidth: "fit-content" }}
              />
              <Stack>
                {plan?.features &&
                  plan.features.length > 0 &&
                  plan.features.map((feature, index) => (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }} key={index}>
                      <Check />
                      <Typography variant="body2">{feature.label}</Typography>
                    </Box>
                  ))}
              </Stack>
              {planPriceLabel && <Typography sx={{ fontWeight: 600 }}>{planPriceLabel}</Typography>}
            </Stack>
          </Box>

          <Alert severity="info" sx={{ alignItems: "center" }}>
            Share your details and our team can contact you about this pricing plan.
          </Alert>

          <TextField
            fullWidth
            label="Name"
            value={form.name}
            onChange={handleChange("name")}
            error={Boolean(errors.name)}
            helperText={errors.name}
          />

          <TextField
            fullWidth
            label="Email"
            type="email"
            value={form.email}
            onChange={handleChange("email")}
            error={Boolean(errors.email)}
            helperText={errors.email}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained">
          Submit Inquiry
        </Button>
      </DialogActions>
    </CustomDialog>
  );
}
